import { cn } from '@foam/buoy/src/lib/utils';
import { useMemo } from 'react';
import {
  NewMetricCard,
  getPlatformMetrics,
  getReadableMetricValue,
} from '~/components/CreatorChannelMetrics';
import { FEATURE_FLAGS } from '~/constants';
import { SupportedPlatforms } from '~/constants/platforms';
import { useGetBenchmarksById } from '~/hooks/api/benchmarks/useGetBenchmarksById';
import { useFetchCreatorPublic } from '~/hooks/api/media-kits/useFetchCreatorPublic';
import { useFeature } from '~/hooks/useFeatureFlag/useFeature';
import { getShareTokenByCreatorSlug } from '~/lib/getShareToken';

import { SingleMetric } from './SingleMetric';

interface Props {
  platform: SupportedPlatforms;
  creatorSlug: string;
  selectedMetrics?: string[];
}

export const AllMetrics: React.FC<Props> = ({
  platform,
  selectedMetrics,
  creatorSlug,
}) => {
  const creator = useFetchCreatorPublic(creatorSlug);
  const benchmarks = useGetBenchmarksById({
    id: creator.data?.benchmark.id,
    shareToken: getShareTokenByCreatorSlug(creator.data?.slug ?? ''),
  });

  const isMetricsCardsEnabled = useFeature(
    FEATURE_FLAGS.foamMetricsCards,
  ).isEnabled;

  const creatorPlatformMetrics = useMemo(() => {
    if (!benchmarks.data) return;

    return getPlatformMetrics(benchmarks.data);
  }, [benchmarks.data, benchmarks.isSuccess]);

  const metricsToShow = (creatorPlatformMetrics?.[platform] ?? []).filter(
    (metric) => metric.value != undefined,
  );

  const titleMapping = {
    'Average Impressions': 'Average Views',
    'Average Reels Impressions': 'Average Reels Views',
    'Average Story Impressions': 'Average Story Views',
  };
  const filteredMetricsToShow = metricsToShow.filter((metric) => {
    const normalizedMetricTitle =
      titleMapping[metric.title as keyof typeof titleMapping] || metric.title;

    return selectedMetrics?.some((selected) => {
      const normalizedSelected =
        titleMapping[selected as keyof typeof titleMapping] || selected;
      return normalizedSelected === normalizedMetricTitle;
    });
  });

  return (
    <>
      <div
        className={cn('grid gap-4', {
          'grid-cols-[repeat(auto-fit,minmax(300px,1fr))]':
            filteredMetricsToShow.length >= 3,
          'grid-cols-[repeat(auto-fit,300px)]':
            filteredMetricsToShow.length < 3,
        })}
      >
        {filteredMetricsToShow.map((metric) =>
          isMetricsCardsEnabled ? (
            <NewMetricCard
              key={metric.title}
              metricTitle={metric.title}
              metricSubtitle={metric.subtitle}
              metricValue={metric.value}
              metricTransformValue={metric.transformValue}
              isDisabled={metric.isDisabled}
              themed
            />
          ) : (
            <SingleMetric
              key={metric.title}
              title={metric.title}
              value={
                getReadableMetricValue(metric.value, metric.transformValue) ||
                '-'
              }
            />
          ),
        )}
      </div>
    </>
  );
};
